import React, {
  type CSSProperties,
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { useTranslation } from '@lt/localization';
import YandexMap from 'src/components/YandexMap';
import { Spinner } from 'src/components/animations/Spinner';
import type { YMap } from '@yandex/ymaps3-types';
import classnames from 'classnames';
import ErrorBoundary from 'src/error/components/ErrorBoundary';
import EmptyBlock from 'src/routes/Hotel/components/EmptyBlock';
import {
  DESKTOP_MAP_TOP_OFFSET,
  DESKTOP_OFFSET_AFTER_HEADER,
  HEADER_OFFSET_SEARCH,
  FULL_MOBILE_OFFSET,
} from 'src/constants/header';
import type {
  Coordinates,
  ShortHotelInfo,
} from 'src/components/YandexMap/types';
import { loadReactComponent } from 'src/components/YandexMap/loaders/componentLoaders';
import { loadModulePromise } from 'src/components/YandexMap/mapLoader';
import { calculateMapCenter } from 'src/components/YandexMap/utils/calculateMapCenter';
import useShortPress from 'src/components/YandexMap/hooks/useShortPress';
import Clusterer from 'src/components/YandexMap/components/Clusterer';
import { countFullDesktopOffset } from 'src/components/YandexMap/utils/countFullDesktopOffset';
import Controls from './components/Controls';
import HotelPopup from './components/HotelPopup';
import {
  ACTIVE_HOTEL_ZOOM,
  DEFAULT_MAP_POSITION,
  DEFAULT_SEARCH_ZOOM,
} from './constants';
import ActiveHotelLayer from './components/ActiveHotelLayer';
import { Container } from './components/Container';
import Point from './components/Point';
import type { IYandexMapsExplore } from './types';

import styles from './yandexMapsExplore.module.css';

const YMapListener = loadReactComponent('YMapListener', loadModulePromise);

const YandexMapsExplore = (props: IYandexMapsExplore) => {
  const {
    hotelsList,
    activeHotel,
    isMobile,
    isExpanded,
    isExpandable,
    toggleHandler,
    resetPopupDataHandler,
    hotelInfoForPopup,
    isHeaderBannerHidden = false,
    isOpen = false,
    marginTop,
    visibleHeight,
    headerBannerOffset,
  } = props;

  const { t } = useTranslation();

  const [isMapLoading, setIsMapLoading] = useState(true);

  const firstHotel = hotelsList?.[0];

  // на эксплоре при маунте карты сразу показываем маркер первого отеля из списка
  const [location, setLocation] = useState(
    firstHotel?.coordinates || DEFAULT_MAP_POSITION,
  );
  const [zoom, setZoom] = useState(ACTIVE_HOTEL_ZOOM);

  const readonlyLocation = useRef(
    firstHotel?.coordinates || DEFAULT_MAP_POSITION,
  );
  const readonlyZoom = useRef(DEFAULT_SEARCH_ZOOM);

  const points = useMemo(
    () =>
      hotelsList.map((hotel) => {
        const { coordinates, ...otherHotelInfo } = hotel;
        return {
          coordinates: coordinates as [number, number],
          shortHotelInfo: otherHotelInfo as ShortHotelInfo,
        };
      }),
    [hotelsList],
  );

  // Зум на отель при наведении на карточку в поиске
  useEffect(() => {
    if (activeHotel) {
      setLocation(activeHotel.coordinates);
      setZoom(ACTIVE_HOTEL_ZOOM);
    }
  }, [activeHotel]);

  // Функция для отслеживания движения юзера по карте
  const onUpdate = useCallback((params) => {
    const { mapInAction, location: currentLocation } = params;
    if (!mapInAction) {
      readonlyLocation.current = currentLocation.center;
      readonlyZoom.current = currentLocation.zoom;
    }
  }, []);

  const mapRef = useRef<YMap>(null);
  const [bounds, setBounds] = useState(mapRef.current?.bounds);

  /**
   * Перемещает позицию карты
   * @param coordinates Координаты центра карты
   * @param zoomDiff Разница в зуме, может быть положительной и отрицательной
   * @param offset Отступ
   */
  const moveToPoint = (
    coordinates: Coordinates,
    zoomDiff: number,
    offset: { x: number; y: number },
  ) => {
    if (!mapRef.current) return;

    // Яндекс не умеет в оффсеты в версии 3.0, поэтому перевычисляем центр карты с учетом отступа
    const coordinatesWithOffset = calculateMapCenter(
      mapRef.current?.bounds[0],
      mapRef.current?.bounds[1],
      coordinates,
      mapRef.current?.size,
      offset,
    );

    setLocation(coordinatesWithOffset);
    setZoom(readonlyZoom.current + zoomDiff);
  };

  const popupRef = useRef<HTMLDivElement>(null);

  const adjustMapToCurrentPosition = () => {
    setLocation(readonlyLocation.current);
    setZoom(readonlyZoom.current);
  };

  // Отслеживаем загрузку тайлов
  // @see https://yandex.ru/dev/jsapi30/doc/ru/dg/concepts/events#handler-interface
  const handleStateChanged = () => {
    setIsMapLoading(false);
  };

  const toggleExpandMap = () => {
    toggleHandler();
    adjustMapToCurrentPosition();
  };

  const onClosePopup = () => {
    resetPopupDataHandler();
    adjustMapToCurrentPosition();
  };

  // Закрываем попап, когда кликаем по карте снаружи попапа
  useShortPress(popupRef, () => {
    if (hotelInfoForPopup) {
      onClosePopup();
    }
  });

  const changeZoom = (zoomStep: number) => {
    if (bounds) {
      setBounds(undefined);
    }
    setZoom(readonlyZoom.current + zoomStep);
  };

  return (
    <Container isMobile={isMobile} isOpen={isOpen}>
      <div
        className={classnames({
          [styles.yandexMapExploreContainer]: !isMobile,
          [styles.mobileYandexMapExploreContainer]: isMobile,
        })}
        style={
          {
            marginTop: marginTop ?? '0px',
            '--desktop-height': `calc(100vh - ${
              countFullDesktopOffset(isHeaderBannerHidden, headerBannerOffset) +
              (visibleHeight || 0)
            }px)`,
            '--desktop-top': `${
              HEADER_OFFSET_SEARCH +
              DESKTOP_OFFSET_AFTER_HEADER -
              DESKTOP_MAP_TOP_OFFSET
            }px`,
            '--mobile-height': `calc(100vh - ${FULL_MOBILE_OFFSET}px)`,
            '--mobile-top': `${FULL_MOBILE_OFFSET}px`,
          } as CSSProperties
        }
      >
        <ErrorBoundary
          fallback={
            <EmptyBlock
              isMobile
              title={t('map.static.message.title')}
              description={t('map.static.message.description')}
            />
          }
          showDialog={false}
        >
          {isMapLoading && (
            <div className={styles.mapLoaderBg}>
              <Spinner size={64} />
            </div>
          )}
          <YandexMap
            isMobile={isMobile}
            center={location}
            zoom={zoom}
            mapRef={mapRef}
          >
            <YMapListener
              onUpdate={onUpdate}
              onStateChanged={handleStateChanged}
            />
            <Controls
              changeZoom={changeZoom}
              isMobile={isMobile}
              isExpanded={isExpanded}
              isExpandable={isExpandable}
              toggleHandler={toggleExpandMap}
            />

            <ActiveHotelLayer
              moveToPoint={moveToPoint}
              coordinates={activeHotel?.coordinates}
            />

            <Clusterer
              isMobile={isMobile}
              moveToPoint={moveToPoint}
              activeHotel={activeHotel}
              points={points}
              Point={Point}
            />
            <HotelPopup popupRef={popupRef} onClosePopup={onClosePopup} />
          </YandexMap>
        </ErrorBoundary>
      </div>
    </Container>
  );
};

export default YandexMapsExplore;
